import { CloseOutlined, TagOutlined } from '@ant-design/icons';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button } from 'antd';
import { noop } from 'lodash';
import { type Map as OlMap } from 'ol';
import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type IMapWidget } from '../../IMapWidget.ts';
import { type IMapLayer } from '../MapLayer.ts';
import { MapLayersTreeInput } from '../tree/MapLayersTreeInput.tsx';
import {
  getGroupNodeKey,
  getLayerNodeKey,
  layerCodeFromNodeKey,
  UNGROUPED_NODE_KEY,
} from '../tree/mapLayersTreeKeys.ts';
import { LayerCard } from './MapLayersToggleControl.styled.tsx';

export interface IMapLayersToggleControlProps {
  map: React.RefObject<OlMap>;
  widget: IMapWidget;
}

// La selezione è un array di chiavi nodo (gruppi + layer). Lo schema è banale:
// non c'è validazione, il form serve solo a ospitare l'input albero e a fornire
// la label accessibile via FormItem.
const visibilitySchema = yup.object({
  visibleLayers: yup.array(yup.string().defined()).default([]),
});

/**
 * Restituisce le chiavi dell'albero dei layer attualmente visibili sulla mappa
 * OpenLayers. I layer OL sono indirizzati per `code` (impostato in init); i
 * layer senza `code` (es. il livello di disegno) vengono ignorati.
 */
function computeVisibleLayerKeys(
  map: React.RefObject<OlMap>,
  layers: IMapLayer[],
): string[] {
  const olLayers = map.current?.getLayers().getArray() ?? [];
  const visibleCodes = new Set(
    olLayers
      .filter(olLayer => olLayer.getVisible())
      .map(olLayer => olLayer.get('code'))
      .filter(Boolean),
  );
  return layers
    .filter(layer => visibleCodes.has(layer.code))
    .map(layer => getLayerNodeKey(layer.code));
}

/**
 * Controllo principale per la gestione della visibilità dei layer nella mappa.
 * Mostra un pulsante "Livelli" che apre il pannello con l'albero checkable di
 * gruppi e layer ({@link MapLayersTreeInput}). La selezione è un campo di form
 * (`visibleLayers`), inizializzato dalla visibilità reale dei layer OpenLayers
 * all'apertura; ad ogni cambio si applica la visibilità su OpenLayers, e il
 * check sul gruppo copre "mostra/nascondi tutti".
 */
export function MapLayersToggleControl(props: IMapLayersToggleControlProps) {
  const { map, widget } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [initialVisibleKeys, setInitialVisibleKeys] = useState<string[]>([]);
  const { t } = useTranslation();

  const ungroupedLabel = t('mappa.livelli.gruppo.altri', 'Altri');

  // widget.groups/layers sono opzionali: senza memo, `?? []` creerebbe un nuovo
  // array a ogni render, ricalcolando inutilmente chiavi e nodi dell'albero.
  const groups = useMemo(() => widget.groups ?? [], [widget.groups]);
  const layers = useMemo(() => widget.layers ?? [], [widget.layers]);

  // Gruppi espansi all'apertura: quelli con expandedByDefault !== false, più la
  // sezione dei livelli senza gruppo. La chiave del nodo virtuale è inclusa
  // sempre: se il nodo non esiste, Ant ignora la chiave espansa.
  const defaultExpandedKeys = useMemo(() => {
    const groupKeys = groups
      .filter(group => group.expandedByDefault !== false)
      .map(group => getGroupNodeKey(group.name));
    return [...groupKeys, UNGROUPED_NODE_KEY];
  }, [groups]);

  // All'apertura la selezione riflette la visibilità corrente dei layer OL:
  // aggiornare initialVisibleKeys fa reinizializzare il form (reset di
  // FormHandler sul cambio di initialValues).
  const openPanel = useCallback(() => {
    setInitialVisibleKeys(computeVisibleLayerKeys(map, layers));
    setIsOpen(true);
  }, [map, layers]);

  // Applica la selezione dell'albero alla visibilità dei layer OpenLayers,
  // indirizzati per `code`. I layer senza `code` vengono ignorati.
  const applyVisibility = useCallback(
    (checked: string[]) => {
      const checkedCodes = new Set(
        checked
          .map(key => layerCodeFromNodeKey(key))
          .filter((code): code is string => code !== null),
      );
      for (const olLayer of map.current?.getLayers().getArray() ?? []) {
        const code = olLayer.get('code');
        if (code) olLayer.setVisible(checkedCodes.has(code));
      }
    },
    [map],
  );

  const initialValues = useMemo(
    () => ({ visibleLayers: initialVisibleKeys }),
    [initialVisibleKeys],
  );

  return (
    <>
      <Button
        type="default"
        id="layer-popup-button"
        style={{ borderWidth: '1px' }}
        icon={<TagOutlined aria-hidden />}
        aria-expanded={isOpen}
        aria-controls="layer-popup-levels"
        onClick={() => (isOpen ? setIsOpen(false) : openPanel())}
      >
        {t('mappa.livelli.pulsante', 'Livelli')}
      </Button>
      <section
        aria-labelledby="layer-popup-button"
        id="layer-popup-levels"
        hidden={!isOpen}
      >
        <LayerCard size="small">
          <FlexRow
            style={{ marginBottom: 6 }}
            right={
              <Button
                title={t('mappa.livelli.popover.pulsante-chiudi', 'Chiudi')}
                size="small"
                type="link"
                icon={<CloseOutlined aria-hidden />}
                onClick={() => {
                  setIsOpen(false);
                }}
              />
            }
          >
            <h3>
              {t(
                'mappa.livelli.popover.titolo.visibilita-dei-livelli',
                'Visibilità dei Livelli',
              )}
            </h3>
          </FlexRow>

          <FormHandler
            nested
            hideVisualFeedback
            schema={visibilitySchema}
            initialValues={initialValues}
            onSubmit={noop}
          >
            <FormItem
              name="visibleLayers"
              label={t(
                'mappa.livelli.popover.form-item-label.visibilita-livelli',
                'Visibilità dei livelli',
              )}
              layout={{
                hideVisualLabel: true,
                removeHelpMargin: true,
                removeMargin: true,
                removeLabelSpace: true,
              }}
            >
              <MapLayersTreeInput
                name="visibleLayers"
                groups={groups}
                layers={layers}
                onAfterChange={applyVisibility}
                defaultExpandedKeys={defaultExpandedKeys}
                ungroupedLabel={ungroupedLabel}
                style={{ maxHeight: 320, overflow: 'auto' }}
              />
            </FormItem>
          </FormHandler>
        </LayerCard>
      </section>
    </>
  );
}
